import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import * as api from '../../../apirest';
import CategoryPills from '../../../shared/components/CategoryPills/CategoryPills';
import Loading from '../../../shared/components/Loading/Loading';
import { categories as PRODUCT_CATEGORIES } from '../../../data/product';
import './StocksPage.css';

const LIMIT = 12;

const StocksPage = () => {
  const [variants, setVariants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const history = useHistory();
  const searchTimeout = useRef(null);
  const [sentinelEl, setSentinelEl] = useState(null);

  // Enrich variants with stock data
  const attachStock = async (list) => {
    return Promise.all(
      list.map(async (v) => {
        try {
          const res = await api.get('/variants/findTotalStocksBySize?', { id: v._id });
          if (res?.data?.[0]) v.groupStock = res.data[0];
        } catch {}
        return v;
      })
    );
  };

  // Base fetch — replace=true resets the list, replace=false appends
  const fetchVariants = useCallback(async ({ name, category, skip, replace }) => {
    const catParam = category !== 'all' ? category : '';
    const url = `/variants?limit=${LIMIT}&name=${encodeURIComponent(name)}&skip=${skip}&categories=${catParam}`;
    try {
      const result = await api.api(url, {});
      const items = result?.variant ?? [];
      const enriched = await attachStock(items);
      const newTotal = result?.total ?? 0;

      if (replace) {
        setVariants(enriched);
      } else {
        setVariants((prev) => [...prev, ...enriched]);
      }
      setTotal(newTotal);
      setHasMore(skip + enriched.length < newTotal);
    } catch {
      toast.error('Erro ao carregar estoques.');
    }
  }, []);

  // Initial load and when search/category changes
  const resetAndLoad = useCallback(
    async (name, category) => {
      setLoading(true);
      await fetchVariants({ name, category, skip: 0, replace: true });
      setLoading(false);
    },
    [fetchVariants]
  );

  useEffect(() => {
    resetAndLoad('', 'all');
  }, [resetAndLoad]);

  // Debounced search
  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearch(val);
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => resetAndLoad(val, activeCategory), 400);
  };

  const clearSearch = () => {
    setSearch('');
    resetAndLoad('', activeCategory);
  };

  // Category pill click
  const handleCategoryChange = (cat) => {
    setActiveCategory(cat);
    resetAndLoad(search, cat);
  };

  // Infinite scroll — load more
  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    await fetchVariants({
      name: search,
      category: activeCategory,
      skip: variants.length,
      replace: false,
    });
    setLoadingMore(false);
  }, [loadingMore, hasMore, fetchVariants, search, activeCategory, variants.length]);

  useEffect(() => {
    if (!sentinelEl) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) loadMore(); },
      { threshold: 0.1 }
    );
    observer.observe(sentinelEl);
    return () => observer.disconnect();
  }, [sentinelEl, loadMore]);

  return (
    <div className="sp-page">
      <ToastContainer />
      <div className="sp-header">
        <h2 className="sp-title">Pesquisar produtos</h2>
        <p className="sp-subtitle">Encontre e gerencie o estoque do catálogo.</p>
      </div>

      <div className="sp-search-wrap">
        <svg className="sp-search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" />
        </svg>
        <input
          className="sp-search-input"
          placeholder="Pesquisar por produto ou cor..."
          value={search}
          onChange={handleSearchChange}
        />
        {search && (
          <button className="sp-search-clear" onClick={clearSearch}>×</button>
        )}
      </div>

      <CategoryPills
        categories={PRODUCT_CATEGORIES}
        activeCategory={activeCategory}
        onChange={handleCategoryChange}
      />

      <div className="sp-meta">
        <span className="sp-count">
          {loading
            ? 'Carregando...'
            : `${total} produto${total !== 1 ? 's' : ''} encontrado${total !== 1 ? 's' : ''}`}
        </span>
      </div>

      {loading ? (
        <Loading overlay />
      ) : variants.length === 0 ? (
        <div className="sp-empty">Nenhum produto encontrado.</div>
      ) : (
        <>
          <div className="sp-grid">
            {variants.map((variant) => (
              <StockCard
                key={variant._id}
                variant={variant}
                onClick={() => history.push(`/admin-panel/EditOrInsertVariantStocks/${variant._id}`)}
              />
            ))}
          </div>

          {/* Infinite scroll sentinel */}
          <div ref={setSentinelEl} className="sp-sentinel">
            {loadingMore && <Loading size="sm" />}
          </div>
        </>
      )}
    </div>
  );
};

const StockCard = ({ variant, onClick }) => {
  const product = variant?.product;
  const stockTotal = variant?.groupStock?.TOTAL ?? 0;

  return (
    <div className="sp-card" onClick={onClick}>
      <div className="sp-card__img-wrap">
        {variant?.pictures?.[0]?.url ? (
          <img
            src={variant.pictures[0].url}
            alt={product?.name}
            className="sp-card__img"
          />
        ) : (
          <div className="sp-card__img-placeholder">Sem imagem</div>
        )}
      </div>
      <div className="sp-card__info">
        <p className="sp-card__name">{product?.name}</p>
        {product?.price != null && (
          <p className="sp-card__price">
            R$ {Number(product.price).toFixed(2).replace('.', ',')}
          </p>
        )}
        {product?.code && <p className="sp-card__ref">Ref: {product.code}</p>}
        {variant?.colorName && <p className="sp-card__color">{variant.colorName}</p>}
        <p className={`sp-card__stock ${stockTotal > 0 ? 'sp-card__stock--ok' : 'sp-card__stock--zero'}`}>
          <span className="sp-card__stock-dot" />
          Estoque: {stockTotal}
        </p>
      </div>
    </div>
  );
};

export default StocksPage;
